import { MouseEvent } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { styled } from '@mui/material'
import { DiscussionTargetType } from '../../../../../domain/entity/DiscussionEntity'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import RoundChatIcon from '../../../../components/icons/RoundChatIcon'

type RowData = {
  uuid: string
  name: string
}

export type ProgressReportNameCellRendererParams =
  ICellRendererParams<RowData> & {
    targetType: DiscussionTargetType
    editable: boolean
  }

export const ProgressReportNameCellRenderer = ({
  data,
  value,
  targetType,
  editable,
}: ProgressReportNameCellRendererParams) => {
  const { startTargetDiscussion } = useDiscussionActionContext()

  if (!data) return null

  const handleIconClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    startTargetDiscussion({
      uuid: data.uuid,
      name: data.name,
      type: targetType,
    })
  }

  const ariaLabelId =
    targetType === 'PROCESS'
      ? 'discussion.trigger.process'
      : 'discussion.trigger.team'

  return (
    <Container>
      <NameText>{value}</NameText>
      {editable && (
        <TriggerButton
          type="button"
          onClick={handleIconClick}
          aria-label={intl.formatMessage({ id: ariaLabelId })}
        >
          <StyledChatIcon />
        </TriggerButton>
      )}
    </Container>
  )
}

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minWidth: 0,
  overflow: 'hidden',
  gap: '6px',
})

const TriggerButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '26px',
  height: '26px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const StyledChatIcon = styled(RoundChatIcon)({
  height: '18px',
  color: colorPalette.monotone[4],
  fill: 'none',
})

const NameText = styled('div')({
  flex: 1,
  minWidth: 0,
  maxHeight: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingLeft: '4px',
})
